import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, Cpu, Pause, RefreshCw, Server } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { cn } from "@/utils/cn";
import { omnimizerApi, omnimizerKeys } from "../api";
import { allocatedGpus } from "../state";
import { lifecycleState } from "../lifecycle";
import type { OmnimizerDeployment } from "../types";
function Kpi({ label, value, icon: Icon, tone, title, overallocated }: { label: string; value: string | number; icon: LucideIcon; tone: string; title?: string; overallocated?: boolean }) {
  return (
    <Card title={title} data-overallocated={overallocated || undefined} className={cn("relative flex flex-col gap-2 overflow-hidden p-3", overallocated && "border-[var(--warning)] bg-[var(--warning)]/10 text-foreground")}>
      <span className={cn("absolute inset-x-0 top-0 h-0.5", tone)} aria-hidden />
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        {label}
        <Icon className="size-4 text-primary" aria-hidden />
      </div>
      <strong className="text-2xl font-semibold tabular-nums">{value}</strong>
    </Card>
  );
}


export function DeploymentKpiStrip({ items, loading = false }: { items: OmnimizerDeployment[]; loading?: boolean }) {
  const { t } = useTranslation();
  const clusters = useQuery({ queryKey: omnimizerKeys.clusters, queryFn: omnimizerApi.listClusters, refetchInterval: 30000 });
  const count = (state: ReturnType<typeof lifecycleState>) => items.filter((d) => lifecycleState(d) === state).length;
  const gpusInUse = items.reduce((sum, d) => sum + allocatedGpus(d), 0);
  const capacity = clusters.data?.every((c) => c.max_gpu_count != null) ? clusters.data.reduce((sum, c) => sum + (c.max_gpu_count || 0), 0) : undefined;
  const overallocated = capacity != null && gpusInUse > capacity;
  const holders = items.filter((d) => allocatedGpus(d) > 0).map((d) => `${d.name} (${allocatedGpus(d)})`).join(", ");
  const dash = (value: number | string) => loading ? "\u2014" : value;
  return <section aria-label={t("omnimizer.overview.kpiLabel")} className="mb-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Kpi label={t("omnimizer.lifecycle.running")} value={dash(count("running"))} icon={Activity} tone="bg-primary" />
        <Kpi label={t("omnimizer.lifecycle.starting")} value={dash(count("starting"))} icon={RefreshCw} tone="bg-chart-2" />
        <Kpi label={t("omnimizer.lifecycle.stopping")} value={dash(count("stopping"))} icon={Pause} tone="bg-chart-2" />
        <Kpi label={t("omnimizer.lifecycle.stopped")} value={dash(count("stopped") + count("sleeping"))} icon={Pause} tone="bg-[var(--warning)]" />
        <Kpi label={t("omnimizer.lifecycle.failed")} value={dash(count("failed"))} icon={AlertTriangle} tone="bg-destructive" />
        <Kpi label={t("omnimizer.overview.gpus")} value={dash(`${gpusInUse} / ${capacity ?? "\u2014"}`)} icon={Cpu} tone={overallocated ? "bg-[var(--warning)]" : "bg-chart-1"} overallocated={overallocated} title={t("omnimizer.lifecycle.gpuHolders", { holders: holders || "—" })} />
        <Kpi
          label={t("omnimizer.overview.clusters")}
          value={clusters.data ? clusters.data.filter((c) => c.status === "ACTIVE").length : "—"}
          icon={Server}
          tone="bg-border"
        />
      </div>


  </section>;
}
